import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';

interface TimeoutConfig extends AxiosRequestConfig {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

interface RequestInfo {
  cancelSource: CancelTokenSource;
  startTime: number;
  url: string;
}

// Almacén global de requests activas
const activeRequests = new Map<string, RequestInfo>();

export function useHttpTimeout() {
  
  /**
   * Cancela todas las requests activas que coincidan con el patrón
   */
  const cancelRequests = (urlPattern?: string) => {
    for (const [key, requestInfo] of activeRequests) {
      if (!urlPattern || requestInfo.url.includes(urlPattern)) {
        console.log(`🚫 Cancelando request: ${requestInfo.url}`);
        requestInfo.cancelSource.cancel(`Request cancelada: ${requestInfo.url}`);
        activeRequests.delete(key);
      }
    }
  };

  /**
   * Hace una request HTTP con timeout, cancelación y retry
   */
  const requestWithTimeout = async <T = any>(
    config: TimeoutConfig,
    requestKey?: string
  ): Promise<T> => {
    const {
      timeout = 15000, // 15 segundos por defecto
      retries = 2,
      retryDelay = 1000,
      ...axiosConfig
    } = config;

    // Generar clave única para esta request
    const key = requestKey || `${config.method || 'GET'}_${config.url}_${Date.now()}`;
    
    // Cancelar request anterior con la misma clave si existe
    if (activeRequests.has(key)) {
      activeRequests.get(key)?.cancelSource.cancel('Nueva request del mismo tipo');
      activeRequests.delete(key);
    }

    let lastError: any;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Crear cancelToken para esta request
        const cancelSource = axios.CancelToken.source();
        
        // Registrar la request activa
        const requestInfo: RequestInfo = {
          cancelSource,
          startTime: Date.now(),
          url: config.url || 'unknown'
        };
        activeRequests.set(key, requestInfo);

        console.log(`🚀 Iniciando request [intento ${attempt + 1}/${retries + 1}]: ${config.url}`);

        // Configurar timeout y cancelToken
        const requestConfig: AxiosRequestConfig = {
          ...axiosConfig,
          timeout,
          cancelToken: cancelSource.token
        };

        // Hacer la request
        const response = await axios(requestConfig);
        
        // Limpiar request exitosa
        activeRequests.delete(key);
        
        const duration = Date.now() - requestInfo.startTime;
        console.log(`✅ Request completada en ${duration}ms: ${config.url}`);
        
        return response.data;
        
      } catch (error: any) {
        // Limpiar request fallida
        activeRequests.delete(key);
        
        lastError = error;
        
        // Si fue cancelada intencionalmente, no reintentar
        if (axios.isCancel(error)) {
          console.log(`🚫 Request cancelada: ${error.message}`);
          throw new Error('Request cancelada');
        }

        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) {
          throw error;
        }
        
        // Si es el último intento, lanzar el error
        if (attempt === retries) {
          console.error(`❌ Request falló después de ${retries + 1} intentos: ${config.url}`, error);
          break;
        }
        
        // Esperar antes del siguiente intento (backoff exponencial)
        const delay = retryDelay * Math.pow(2, attempt);
        console.log(`⏳ Reintentando en ${delay}ms... (intento ${attempt + 1}/${retries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // Si llegamos aquí, todos los intentos fallaron
    throw lastError;
  };

  /**
   * Obtiene información sobre requests activas
   */
  const getActiveRequests = () => {
    const requests = Array.from(activeRequests.entries()).map(([key, info]) => ({
      key,
      url: info.url,
      duration: Date.now() - info.startTime
    }));
    return requests;
  };

  /**
   * Cancela requests que llevan demasiado tiempo
   */
  const cancelLongRunningRequests = (maxDuration = 30000) => {
    const now = Date.now();
    for (const [key, requestInfo] of activeRequests) {
      if (now - requestInfo.startTime > maxDuration) {
        console.log(`⏰ Cancelando request por timeout (${now - requestInfo.startTime}ms): ${requestInfo.url}`);
        requestInfo.cancelSource.cancel(`Timeout después de ${maxDuration}ms`);
        activeRequests.delete(key);
      }
    }
  };

  return {
    requestWithTimeout,
    cancelRequests,
    getActiveRequests,
    cancelLongRunningRequests
  };
}
